import { EventTimeline, MatrixClient } from 'matrix-js-sdk/src/matrix';
import { useEffect } from 'react';

import { endMeeting } from 'api/meedioBackend/endMeeting';
import { MeetingResponse } from 'api/meedioBackend/types';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import {
  announceMeetingLive,
  clearStartNotification,
  hasRecentMeetingStartedEvent,
  isStartNotified,
  notifyScheduledMeetingStarted,
} from 'hooks/useNotifyScheduledMeetingStarted/useNotifyScheduledMeetingStarted';
import useScheduledMeetings from 'hooks/useScheduledMeetings/useScheduledMeetings';
import { FeatureFlag } from 'utils/Constants';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, markCallRoomEnded } from 'utils/matrixUtils';
import {
  getCurrentOrNextOccurrenceWindow,
  getMostRecentPastOccurrenceWindow,
  JOIN_PREROOM_THRESHOLD_MS,
  OccurrenceWindow,
} from 'utils/occurrenceWindow';

const MAX_TIMEOUT_MS = 2_147_483_647;
// Dedup window for MEETING_ENDED events. The T2 retrospective cleanup fires at
// the scheduled `ends_at` boundary; if the host manually ended the meeting
// earlier (from banner or sidebar), both paths would emit MEETING_ENDED.
// Using 24 h effectively makes the window session-scoped: once ANY
// MEETING_ENDED exists for the current occurrence, the T2 path skips.
const MEETING_ENDED_DEDUP_WINDOW_MS = 24 * 60 * 60 * 1000;

// BUG-037: call rooms whose current occurrence ended THIS session. Survives the
// `clearStartNotification` call inside `fireScheduledEnd` (which wipes the
// `startNotifiedRooms` dedup guard), blocking `fireScheduledStart` from re-announcing
// the same occurrence when the trigger effect re-runs after a `useScheduledMeetings`
// refetch — before the MEETING_ENDED event has synced into the local timeline.
//
// BUG-008 Fix A: changed from Set<string> to Map<string, number> keyed by
// matrixRoomId, value = the occurrence's startMs that ended (or a Date.now()
// approximation). The trigger loop only clears the guard when the new occurrence's
// startMs is strictly later than the ended occurrence's startMs + one preroom
// threshold — preventing premature clearing for the current (early-ended) occurrence.
const endedThisSessionRooms = new Map<string, number>();

// Test-only: reset the module-level end-this-session guard between cases.
export const __resetEndedThisSessionRooms = (): void => {
  endedThisSessionRooms.clear();
};

// BUG-045: called by external end paths (banner auto-cleanup, sidebar "End meeting")
// to populate endedThisSessionRooms immediately, blocking fireScheduledStart from
// re-announcing the meeting before the backend `ended_at` propagates via polling.
// `occurrenceStartMs` anchors the guard to the specific occurrence; when unknown,
// Date.now() is a safe approximation (host end is always within the pre-room window).
export const markMeetingEndedThisSession = (matrixRoomId: string, occurrenceStartMs?: number): void => {
  endedThisSessionRooms.set(matrixRoomId, occurrenceStartMs ?? Date.now());
};

let T2_STATE_SYNC_BACK_WAIT_MS = 1500;

export const __setT2TestDelays = (syncBackMs: number): void => {
  T2_STATE_SYNC_BACK_WAIT_MS = syncBackMs;
};

// True when a MEETING_ENDED for `matrixRoomId` exists on the messenger timeline at
// or after `floorMs`. Callers pass an occurrence-window start to scope the check to
// a single occurrence, or `Date.now() - MEETING_ENDED_DEDUP_WINDOW_MS` for the
// session-scoped recent-end check.
export const hasMeetingEndedEvent = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  matrixRoomId: string,
  floorMs: number
): boolean => {
  const events = matrixClient.getRoom(messengerRoomId)?.getLiveTimeline?.().getEvents?.();
  if (!events || events.length === 0) return false;
  for (const event of events) {
    if (event.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) continue;
    if (event.getContent()?.meeting_room_id !== matrixRoomId) continue;
    if (event.getTs() >= floorMs) return true;
  }
  return false;
};

// BUG-008 Fix C: true when the messenger timeline contains a MEETING_ENDED with
// `ended_by` (explicit host action, §6.2) for the given call room in [floorMs, ceilMs).
// Unlike hasMeetingEndedEvent (which catches all ends), this only matches owner-explicit
// ends — "last person leaves" (§6.3) has no ended_by and must NOT block re-announce
// for the same slot.
const hasExplicitHostEndEvent = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  matrixRoomId: string,
  floorMs: number,
  ceilMs: number
): boolean => {
  const events = matrixClient.getRoom(messengerRoomId)?.getLiveTimeline?.().getEvents?.();
  if (!events || events.length === 0) return false;
  for (const event of events) {
    if (event.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) continue;
    const content = event.getContent() ?? {};
    if (content.meeting_room_id !== matrixRoomId) continue;
    if (!content.ended_by) continue;
    const ts = event.getTs();
    if (ts >= floorMs && ts < ceilMs) return true;
  }
  return false;
};

const claimedClearStateLeadership = async (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  ourEventId: string
): Promise<boolean> => {
  await new Promise((resolve) => setTimeout(resolve, T2_STATE_SYNC_BACK_WAIT_MS));
  const currentStateEvent = matrixClient
    .getRoom(messengerRoomId)
    ?.getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
  return currentStateEvent?.getId() === ourEventId;
};

const getMessengerCallStateRoom = (matrixClient: MatrixClient, messengerRoomId: string) =>
  matrixClient.getRoom(messengerRoomId)?.getLiveTimeline().getState(EventTimeline.FORWARDS);

const isMessengerCallStateLiveFor = (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  matrixRoomId: string
): { live: boolean; content: Record<string, unknown> | undefined } => {
  const stateEvent = getMessengerCallStateRoom(matrixClient, messengerRoomId)?.getStateEvents(
    CustomMeedioMatrixEvents.MESSENGER_CALL,
    ''
  );
  const content = stateEvent?.getContent() ?? undefined;
  const callRoomId = content?.call_room_id as string | undefined;
  return { live: callRoomId === matrixRoomId, content };
};

const fireScheduledStart = async (
  matrixClient: MatrixClient,
  meeting: MeetingResponse,
  window: OccurrenceWindow
): Promise<void> => {
  if (!meeting.messenger_room_id) return;
  // BUG-037 (Fix B): this occurrence already ended this session — never re-announce.
  // Survives clearStartNotification (which fireScheduledEnd calls), unlike isStartNotified.
  if (endedThisSessionRooms.has(meeting.matrix_room_id)) return;
  // BUG-037 (Fix A): the IS backend confirmed an end within the current occurrence
  // window. Persists across page reloads / remounts (the in-memory Map does not).
  if (meeting.ended_at) {
    const endedAtMs = Date.parse(meeting.ended_at);
    if (endedAtMs >= window.startMs - JOIN_PREROOM_THRESHOLD_MS) return;
  }
  // BUG-008 Fix C: owner explicitly ended this occurrence (ended_by present in timeline).
  // Per §6.2 (business flows), owner "End meeting" is terminal — the slot must not
  // reopen at scheduled_at. Distinct from §6.3 "last person leaves" which has no
  // ended_by and correctly leaves the slot open until ends_at.
  if (
    hasExplicitHostEndEvent(
      matrixClient,
      meeting.messenger_room_id,
      meeting.matrix_room_id,
      window.startMs - JOIN_PREROOM_THRESHOLD_MS,
      window.endMs
    )
  ) {
    return;
  }
  // BUG-018: drop redundant re-runs synchronously. The effect re-runs on every
  // scheduled-meetings refresh; without this guard each run re-enters the async
  // announce path (and its getMeetingByRoomId call) before the in-process dedup
  // set is consulted. Meetings skipped by the BUG-016 guard below are
  // intentionally absent from the set, so they are still retried here.
  if (isStartNotified(meeting.matrix_room_id)) return;
  if (Date.now() >= window.endMs) return;

  // BUG-010: widen the guard floor by JOIN_PREROOM_THRESHOLD_MS to catch host-end
  // events emitted during the pre-room window (up to 15 min before startMs). Without
  // this, an end event at e.g. 21:59 for a 22:00 meeting is invisible to the guard
  // and fireScheduledStart emits a new MEETING_STARTED at the original scheduled time.
  // Mirrors the BUG-005 fix in useNotifyScheduledMeetingStarted.
  if (
    hasMeetingEndedEvent(
      matrixClient,
      meeting.messenger_room_id,
      meeting.matrix_room_id,
      window.startMs - JOIN_PREROOM_THRESHOLD_MS
    )
  ) {
    return;
  }

  const { live, content } = isMessengerCallStateLiveFor(
    matrixClient,
    meeting.messenger_room_id,
    meeting.matrix_room_id
  );
  if (live) return;
  // BUG-016: another meeting already owns the MESSENGER_CALL singleton. Starting
  // this one would overwrite that state on every client (evicting the live
  // meeting's banner + resetting its timer). Skip — the meeting still surfaces in
  // the banner via the scheduled-window / RTC passes, and becomes primary once
  // the slot frees.
  const existingPrimaryRoomId = content?.call_room_id as string | undefined;
  if (existingPrimaryRoomId) return;

  const currentUserId = matrixClient.getUserId();
  const isOrganizer = !!currentUserId && meeting.created_by_mxid === currentUserId;

  try {
    if (isOrganizer) {
      await notifyScheduledMeetingStarted({ matrixClient, matrixRoomId: meeting.matrix_room_id, meeting });
    } else {
      await announceMeetingLive({ matrixClient, matrixRoomId: meeting.matrix_room_id, meeting });
    }
  } catch (error) {
    logger.warn('useScheduledMeetingTriggers: T1 emit failed', { error, meetingId: meeting.id });
  }
};

const fireScheduledEnd = async (matrixClient: MatrixClient, meeting: MeetingResponse): Promise<void> => {
  if (!meeting.messenger_room_id) return;
  const messengerRoomId = meeting.messenger_room_id;

  const callRoom = matrixClient.getRoom(meeting.matrix_room_id);
  const rtcSession = callRoom ? matrixClient.matrixRTC.getActiveRoomSession(callRoom) : null;
  const hasActiveRtc = !!rtcSession && rtcSession.memberships.length > 0;
  if (hasActiveRtc) return;

  // `content` is read early for `started_at` derivation later in this function.
  // Do NOT use `live` from this read to decide whether to clear MESSENGER_CALL —
  // an instant call can take over the slot during the `await endMeeting()` below,
  // making a stale `live === true` erase the instant room's slot (BUG-003 TOCTOU).
  // The fresh read immediately before `sendStateEvent` (below) is the guard.
  const { content } = isMessengerCallStateLiveFor(matrixClient, messengerRoomId, meeting.matrix_room_id);
  const peerAlreadyEnded = hasMeetingEndedEvent(
    matrixClient,
    messengerRoomId,
    meeting.matrix_room_id,
    Date.now() - MEETING_ENDED_DEDUP_WINDOW_MS
  );

  let endedAt: string | null = null;
  try {
    const result = await endMeeting(meeting.id);
    endedAt = result.ended_at;
  } catch (error) {
    logger.warn('useScheduledMeetingTriggers: T2 endMeeting failed', { error, meetingId: meeting.id });
  }

  // BUG-003: Re-read MESSENGER_CALL ownership after the endMeeting await. The slot
  // may have been taken over by an instant call during the network round-trip;
  // using a stale read would clear the instant room's slot, triggering Guard A in
  // ActiveRoom and kicking instant-call participants. Mirrors Guard B.2
  // (MeetingSidebarView) and B.1 (useMessengerCallHangup).
  const { live: liveNow } = isMessengerCallStateLiveFor(matrixClient, messengerRoomId, meeting.matrix_room_id);
  let ourClearEventId: string | null = null;
  if (liveNow) {
    try {
      const result = await matrixClient.sendStateEvent(
        messengerRoomId,
        CustomMeedioMatrixEvents.MESSENGER_CALL,
        {},
        ''
      );
      ourClearEventId = result?.event_id ?? null;
    } catch (error) {
      logger.warn('useScheduledMeetingTriggers: T2 state clear failed', { error, meetingId: meeting.id });
    }
  }

  const shouldEmitTimeline = !!endedAt && !peerAlreadyEnded;
  if (!shouldEmitTimeline) {
    clearStartNotification(meeting.matrix_room_id);
    return;
  }

  if (ourClearEventId) {
    const weWon = await claimedClearStateLeadership(matrixClient, messengerRoomId, ourClearEventId);
    if (!weWon) {
      clearStartNotification(meeting.matrix_room_id);
      return;
    }
  }

  if (
    hasMeetingEndedEvent(
      matrixClient,
      messengerRoomId,
      meeting.matrix_room_id,
      Date.now() - MEETING_ENDED_DEDUP_WINDOW_MS
    )
  ) {
    clearStartNotification(meeting.matrix_room_id);
    return;
  }

  const startedAtFromState = content?.started_at as string | undefined;
  // Supplement (FR-4d): If we are about to emit MEETING_ENDED and no
  // MEETING_STARTED has landed recently for this meeting_room_id, emit a
  // paired start first. Covers the case where T1 never ran (no client had
  // this chat open at scheduled_at). Synthetic FE backfill handles the
  // zero-FE-online case; this handles "at least one client running T2".
  const messengerRoomForCheck = matrixClient.getRoom(messengerRoomId) ?? null;
  // BUG-002: anchor the paired-emit dedup to the past occurrence window's start
  // rather than the legacy 60-second rolling default. Prevents the paired emit
  // from firing again when a real MEETING_STARTED for the same occurrence is
  // already on the timeline (which is common for long-running meetings).
  const pastWindowForDedup = getMostRecentPastOccurrenceWindow(meeting, new Date());
  const startedFloorMs = pastWindowForDedup ? pastWindowForDedup.startMs : undefined;
  if (!hasRecentMeetingStartedEvent(messengerRoomForCheck, meeting.matrix_room_id, startedFloorMs)) {
    const fallbackStartedAtMs = endedAt ? Date.parse(endedAt) - 1000 : Date.now() - 1000;
    const startedAtIso = startedAtFromState ?? new Date(fallbackStartedAtMs).toISOString();
    try {
      await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.MEETING_STARTED, {
        meeting_room_id: meeting.matrix_room_id,
        started_at: startedAtIso,
        started_by: meeting.created_by_mxid,
        meeting_id: meeting.id,
        title: meeting.room_name ?? '',
      });
    } catch (error) {
      logger.warn('useScheduledMeetingTriggers: T2 paired MEETING_STARTED emit failed', {
        error,
        meetingId: meeting.id,
      });
    }
  }

  const startedAt = startedAtFromState;
  const duration = startedAt && endedAt ? Math.floor((Date.parse(endedAt) - Date.parse(startedAt)) / 1000) : 0;
  try {
    await matrixClient.sendEvent(messengerRoomId, CustomMeedioMatrixEvents.MEETING_ENDED, {
      meeting_room_id: meeting.matrix_room_id,
      ended_at: endedAt,
      // BUG-007: T2 occurrence-window closure has no acting user — omit `ended_by`
      // so the chat row reads "<Meeting> ended" rather than "ended by <Name>".
      // Only MeetingSidebarView's explicit "End meeting" carries `ended_by`.
      duration,
      meeting_id: meeting.id,
      meeting_name: meeting.room_name ?? '',
      dedupe_key: `${messengerRoomId}|${meeting.matrix_room_id}|meeting_ended`,
      source: 'immediate',
    });
  } catch (error) {
    logger.warn('useScheduledMeetingTriggers: T2 MEETING_ENDED emit failed', { error, meetingId: meeting.id });
  }

  // Stamp the call room as ended so every client knows it's done without
  // depending on MEETING_ENDED timeline pagination.
  const schedEnded = endedAt ?? new Date().toISOString();
  await markCallRoomEnded(matrixClient, meeting.matrix_room_id, messengerRoomId, schedEnded, 'scheduled_end');

  // BUG-037: record the end BEFORE clearing the start-notification guard so a
  // same-tick refetch re-run of fireScheduledStart sees the block and does not
  // re-announce this occurrence. BUG-008 Fix A: store the occurrence startMs so
  // the trigger loop can distinguish same-occurrence vs next-occurrence when
  // deciding whether to lift the guard.
  const endedOccurrenceWindow =
    getMostRecentPastOccurrenceWindow(meeting, new Date()) ?? getCurrentOrNextOccurrenceWindow(meeting);
  endedThisSessionRooms.set(meeting.matrix_room_id, endedOccurrenceWindow?.startMs ?? Date.now());
  clearStartNotification(meeting.matrix_room_id);
};

const useScheduledMeetingTriggers = (messengerRoomId: string | null | undefined): void => {
  const { matrixClient } = useMatrixContext();
  const { meetings } = useScheduledMeetings(messengerRoomId ?? null);
  const isScheduledEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER_SCHEDULED_MEETINGS);

  useEffect(() => {
    if (!messengerRoomId) return undefined;
    if (!isScheduledEnabled) return undefined;
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (const meeting of meetings) {
      if (meeting.cancelled_at) continue;
      if (!meeting.messenger_room_id) continue;

      const now = Date.now();

      const pastWindow = getMostRecentPastOccurrenceWindow(meeting, new Date(now));
      if (pastWindow && pastWindow.endMs <= now && meeting.messenger_room_id) {
        const { live } = isMessengerCallStateLiveFor(matrixClient, meeting.messenger_room_id, meeting.matrix_room_id);
        if (live) {
          fireScheduledEnd(matrixClient, meeting).catch(() => undefined);
        }
      }

      const window = getCurrentOrNextOccurrenceWindow(meeting, new Date(now));
      if (!window) continue;

      const { startMs, endMs } = window;

      if (startMs > now) {
        // BUG-037: the resolved window is a FUTURE occurrence — the previous
        // occurrence (if any) is over, so lift its re-announce block (the call room
        // id is shared across recurring occurrences) and let this one announce when
        // it starts. The `meeting.ended_at` guard still blocks re-announce of the
        // just-ended occurrence (its end falls before this future window's start).
        //
        // BUG-008 Fix A: only lift the guard when the occurrence we're about to
        // schedule is genuinely LATER than the one that was ended (i.e. a new
        // recurring occurrence). If `startMs` is still the same occurrence (e.g.
        // the effect re-ran before wall-clock start after an early host end), keep
        // the guard so fireScheduledStart doesn't re-announce at scheduled_at.
        const endedOccStartMs = endedThisSessionRooms.get(meeting.matrix_room_id);
        if (endedOccStartMs === undefined || startMs > endedOccStartMs + JOIN_PREROOM_THRESHOLD_MS) {
          endedThisSessionRooms.delete(meeting.matrix_room_id);
        }
        const delay = startMs - now;
        if (delay <= MAX_TIMEOUT_MS) {
          timers.push(setTimeout(() => fireScheduledStart(matrixClient, meeting, window), delay));
        }
      } else if (startMs <= now && now < endMs) {
        fireScheduledStart(matrixClient, meeting, window).catch(() => undefined);
      }

      if (endMs > now) {
        const delay = endMs - now;
        if (delay <= MAX_TIMEOUT_MS) {
          timers.push(setTimeout(() => fireScheduledEnd(matrixClient, meeting), delay));
        }
      }
    }

    return () => timers.forEach(clearTimeout);
  }, [isScheduledEnabled, matrixClient, messengerRoomId, meetings]);
};

export default useScheduledMeetingTriggers;
